import { Controller } from "@hotwired/stimulus"

// Buyout package show page — Documents | Invites | Q&A | Bids sub-tabs,
// chip-based document uploads (presign → S3 PUT → register), invite modal
// (multi-row manual entry; sub-directory picker lands in a later stage),
// open/close status transitions, scope-sheet inline save. All DOM-in-place
// updates via fetch — no reloads.
export default class extends Controller {
  static targets = [
    "headerName", "headerTrade", "headerDue", "headerEstimate", "statusPill",
    "openBtn", "closeBtn",
    "scopeTextarea", "scopeSaveBtn",
    "tabBtn", "tabPanel",
    "kindRadio", "uploadNote", "dropzone", "fileInput", "chipList",
    "docsWrap", "docsList", "docsEmpty",
    "invitesWrap", "invitesTbody", "invitesEmpty", "invitesCount",
    "packageModal", "packageModalTitle", "packageModalError", "packageSaveBtn",
    "pkgName", "pkgTrade", "pkgDue", "pkgEstimate", "pkgScope",
    "inviteModal", "inviteRows", "inviteRowTemplate", "inviteModalError", "inviteSendBtn",
    "pickerSearch", "pickerShowAll", "pickerList", "pickerRow", "pickerCheckbox", "pickerNoMatches",
    "docDeleteModal", "docDeleteName",
    "inviteDeleteModal", "inviteDeleteName",
    "questionsList", "questionsEmpty",
    "qaModal", "qaModalQuestion", "qaModalAnswer", "qaModalError", "qaModalSaveBtn",
    "publishModal", "publishConfirmBtn",
    "bidsPanel", "awardBanner", "notifyListSource",
    "revisionModal", "revisionName", "revisionMessage", "revisionError", "revisionSendBtn",
    "awardModal", "awardModalBody", "awardError", "awardConfirmBtn",
    "notifyModal", "notifyModalBody", "notifyError", "notifyConfirmBtn",
    "undoAwardModal", "undoAwardConfirmBtn",
    "aiCheckPanel", "aiCheckBtn"
  ]

  static values = {
    updateUrl: String,
    openUrl: String,
    closeUrl: String,
    scopeUrl: String,
    presignUrl: String,
    registerUrl: String,
    docDeleteUrlTemplate: String,    // ".../documents/0"
    invitesUrl: String,
    inviteResendUrlTemplate: String, // ".../invites/0/resend"
    inviteDeleteUrlTemplate: String, // ".../invites/0"
    questionCreateUrl: String,
    questionAnswerUrlTemplate: String,  // ".../questions/0/answer"
    questionPublishUrlTemplate: String, // ".../questions/0/publish"
    revisionUrlTemplate: String,        // ".../invites/0/request_revision"
    awardUrl: String,
    undoAwardUrl: String,
    notifyLosersUrl: String,
    aiCheckUrl: String
  }

  connect() {
    this._pendingDocId = null
    this._pendingInviteId = null
    this._pendingQuestionId = null
    this._pendingRevisionInviteId = null
    this._pendingAwardBidId = null
  }

  // ── Sub-tabs ────────────────────────────────────────────────────────

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.tabBtnTargets.forEach(btn => {
      const active = btn.dataset.tab === tab
      btn.classList.toggle("text-brand", active)
      btn.classList.toggle("font-bold", active)
      btn.classList.toggle("text-text-muted", !active)
      btn.classList.toggle("font-medium", !active)
    })
    this.tabPanelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.tab !== tab)
    })
  }

  // ── Status transitions ──────────────────────────────────────────────

  openPackage(event) {
    this._transition(event, this.openUrlValue, "Package opened — invited subs can now view and bid")
  }

  closePackage(event) {
    this._transition(event, this.closeUrlValue, "Package closed — subs can no longer submit bids")
  }

  async _transition(event, url, successMessage) {
    event.preventDefault()
    try {
      const res = await fetch(url, { method: "POST", headers: this._jsonHeaders() })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Something went wrong")

      this.statusPillTarget.innerHTML = data.pill
      const open = data.status === "open"
      this.openBtnTarget.classList.toggle("hidden", open)
      this.closeBtnTarget.classList.toggle("hidden", !open)
      this._showToast(successMessage)
    } catch (e) {
      this._showToast(e.message, "error")
    }
  }

  // ── Scope sheet ─────────────────────────────────────────────────────

  scopeChanged() {
    this.scopeSaveBtnTarget.classList.remove("hidden")
  }

  async saveScope(event) {
    event.preventDefault()
    try {
      const res = await fetch(this.scopeUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ scope_sheet: this.scopeTextareaTarget.value })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to save scope sheet")
      this.scopeSaveBtnTarget.classList.add("hidden")
      this._showToast("Scope sheet saved")
    } catch (e) {
      this._showToast(e.message, "error")
    }
  }

  // ── Edit package modal ──────────────────────────────────────────────

  showEditModal(event) {
    event.preventDefault()
    const d = this.element.dataset
    this.packageModalTitleTarget.textContent = "Edit Package"
    this.pkgNameTarget.value = d.packageName || ""
    this.pkgTradeTarget.value = d.packageTrade || ""
    this._setDate(d.packageDue || "")
    this.pkgEstimateTarget.value = d.packageEstimate || ""
    this.pkgScopeTarget.value = this.scopeTextareaTarget.value
    this._clearError(this.packageModalErrorTarget)
    this.packageModalTarget.classList.remove("hidden")
    this.pkgNameTarget.focus()
  }

  hidePackageModal(event) {
    event?.preventDefault()
    this.packageModalTarget.classList.add("hidden")
  }

  async savePackage(event) {
    event.preventDefault()
    const name = this.pkgNameTarget.value.trim()
    if (!name) {
      this._setError(this.packageModalErrorTarget, "Give the package a name.")
      return
    }

    this.packageSaveBtnTarget.disabled = true
    try {
      const res = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          bid_package: {
            name,
            trade: this.pkgTradeTarget.value.trim(),
            bids_due_at: this.pkgDueTarget.value.trim(),
            estimate_amount: this.pkgEstimateTarget.value.trim(),
            scope_sheet: this.pkgScopeTarget.value
          }
        })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Something went wrong")

      const pkg = data.package || {}
      this.headerNameTarget.textContent = pkg.name || name
      this.headerTradeTarget.textContent = pkg.trade || "No trade set"
      this.headerDueTarget.textContent = pkg.bids_due || "not set"
      this.headerEstimateTarget.textContent = pkg.estimate || "not set"
      this.scopeTextareaTarget.value = this.pkgScopeTarget.value
      this.scopeSaveBtnTarget.classList.add("hidden")
      const d = this.element.dataset
      d.packageName = pkg.name || name
      d.packageTrade = pkg.trade || ""
      d.packageDue = pkg.bids_due_value || ""
      d.packageEstimate = pkg.estimate_value || ""
      this.hidePackageModal()
      this._showToast("Package updated")
    } catch (e) {
      this._setError(this.packageModalErrorTarget, e.message)
    } finally {
      this.packageSaveBtnTarget.disabled = false
    }
  }

  // ── Documents: chip upload (presign → S3 PUT → register) ────────────

  triggerFileInput(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  handleFiles(event) {
    const files = Array.from(event.target.files || [])
    if (files.length) this._uploadFiles(files)
    event.target.value = ""
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-brand", "bg-blue-50")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50")
  }

  dropFiles(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50")
    const files = Array.from(event.dataTransfer?.files || [])
    if (files.length) this._uploadFiles(files)
  }

  async _uploadFiles(files) {
    const chips = files.map(file => {
      const chip = this._buildUploadingChip(file.name)
      this.chipListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/octet-stream",
            byte_size: f.size
          }))
        })
      })
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }
      const { uploads } = await presignRes.json()

      // Parallel PUTs straight to S3 (project convention).
      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const file = files[i]
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || upload.content_type },
            body: file
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return { upload, file }
        })
      )
      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("Upload failed — please try again")

      const kind = this.kindRadioTargets.find(r => r.checked)?.value || "document"
      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          kind,
          note: this.uploadNoteTarget.value.trim(),
          files: successful.map(({ upload, file }) => ({
            cache_name: upload.cache_name,
            filename: upload.filename,
            content_type: file.type || upload.content_type,
            byte_size: file.size
          }))
        })
      })
      const data = await registerRes.json().catch(() => ({}))
      if (!registerRes.ok || !data.ok) throw new Error(data.error || "Failed to register uploads")

      data.documents.forEach(doc => {
        this.docsListTarget.insertAdjacentHTML("afterbegin", doc.row)
      })
      this.docsWrapTarget.classList.remove("hidden")
      this.docsEmptyTarget.classList.add("hidden")
      this.uploadNoteTarget.value = ""

      const failed = files.length - successful.length
      const noun = data.documents.length === 1 ? "document" : "documents"
      this._showToast(failed > 0
        ? `${data.documents.length} ${noun} uploaded — ${failed} failed`
        : `${data.documents.length} ${noun} uploaded`, failed > 0 ? "error" : "success")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      chips.forEach(chip => chip.remove())
    }
  }

  _buildUploadingChip(name) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[13px] font-medium"
    chip.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>'
    const label = document.createElement("span")
    label.className = "truncate max-w-[220px]"
    label.textContent = name
    chip.appendChild(label)
    return chip
  }

  // ── Document delete (styled confirm modal) ──────────────────────────

  showDocDeleteModal(event) {
    event.preventDefault()
    this._pendingDocId = event.currentTarget.dataset.docId
    this.docDeleteNameTarget.textContent = event.currentTarget.dataset.docName || "This document"
    this.docDeleteModalTarget.classList.remove("hidden")
  }

  hideDocDeleteModal(event) {
    event?.preventDefault()
    this.docDeleteModalTarget.classList.add("hidden")
    this._pendingDocId = null
  }

  async confirmDocDelete(event) {
    event.preventDefault()
    const id = this._pendingDocId
    if (!id) return

    try {
      const res = await fetch(this._fromTemplate(this.docDeleteUrlTemplateValue, id), {
        method: "DELETE",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to delete document")

      document.getElementById(`bid-package-doc-${id}`)?.remove()
      if (!this.docsListTarget.querySelector("li")) {
        this.docsWrapTarget.classList.add("hidden")
        this.docsEmptyTarget.classList.remove("hidden")
      }
      this._showToast("Document deleted")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.hideDocDeleteModal()
    }
  }

  // ── Invites ─────────────────────────────────────────────────────────

  showInviteModal(event) {
    event.preventDefault()
    if (!this.inviteRowsTarget.querySelector("[data-invite-row]")) this.addInviteRow()
    this._clearError(this.inviteModalErrorTarget)
    this.inviteModalTarget.classList.remove("hidden")
  }

  hideInviteModal(event) {
    event?.preventDefault()
    this.inviteModalTarget.classList.add("hidden")
  }

  addInviteRow(event) {
    event?.preventDefault()
    const clone = this.inviteRowTemplateTarget.content.cloneNode(true)
    this.inviteRowsTarget.appendChild(clone)
  }

  removeInviteRow(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-invite-row]")?.remove()
  }

  // Directory picker (feature 10) — search + "show all trades" toggle.
  // Rows are server-rendered with data-covers-trade / data-picker-search;
  // checked vendors stay visible even when filtered out.
  filterPicker() {
    if (!this.hasPickerListTarget) return
    const query = (this.hasPickerSearchTarget ? this.pickerSearchTarget.value : "").trim().toLowerCase()
    const showAll = this.hasPickerShowAllTarget ? this.pickerShowAllTarget.checked : true
    let visible = 0

    this.pickerRowTargets.forEach(row => {
      const matchesQuery = !query || (row.dataset.pickerSearch || "").includes(query)
      const matchesTrade = showAll || row.dataset.coversTrade === "true"
      const checked = row.querySelector("input[type=checkbox]")?.checked
      const show = checked || (matchesQuery && matchesTrade)
      row.classList.toggle("hidden", !show)
      if (show) visible++
    })
    this.pickerNoMatchesTarget.classList.toggle("hidden", visible > 0)
  }

  async sendInvites(event) {
    event.preventDefault()

    // Directory picks — the server copies the vendor's contact fields and
    // stamps sub_vendor_id on the invite.
    const picked = this.hasPickerCheckboxTarget
      ? this.pickerCheckboxTargets.filter(cb => cb.checked).map(cb => ({
          sub_vendor_id: cb.dataset.vendorId,
          company_name: (cb.dataset.vendorName || "").trim(),
          contact_name: (cb.dataset.vendorContactName || "").trim(),
          email: (cb.dataset.vendorEmail || "").trim(),
          phone: (cb.dataset.vendorPhone || "").trim()
        }))
      : []

    const rows = Array.from(this.inviteRowsTarget.querySelectorAll("[data-invite-row]"))
    const manual = rows.map(row => ({
      company_name: row.querySelector('[data-field="company_name"]').value.trim(),
      contact_name: row.querySelector('[data-field="contact_name"]').value.trim(),
      email: row.querySelector('[data-field="email"]').value.trim(),
      phone: row.querySelector('[data-field="phone"]').value.trim(),
      save_to_directory: !!row.querySelector('[data-field="save_to_directory"]')?.checked
    })).filter(i => i.email)

    const invites = picked.concat(manual)
    if (invites.length === 0) {
      this._setError(this.inviteModalErrorTarget, "Pick subs from your directory or add at least one with an email address.")
      return
    }

    this.inviteSendBtnTarget.disabled = true
    this.inviteSendBtnTarget.textContent = "Sending..."
    try {
      const res = await fetch(this.invitesUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ invites })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to send invites")

      data.invites.forEach(invite => {
        this.invitesTbodyTarget.insertAdjacentHTML("beforeend", invite.row)
      })
      this.invitesWrapTarget.classList.remove("hidden")
      this.invitesEmptyTarget.classList.add("hidden")
      if (this.hasInvitesCountTarget) this.invitesCountTarget.textContent = data.invites_count
      this.inviteRowsTarget.innerHTML = ""
      if (this.hasPickerCheckboxTarget) {
        this.pickerCheckboxTargets.forEach(cb => { cb.checked = false })
        this.filterPicker()
      }
      this.hideInviteModal()
      const noun = data.invites.length === 1 ? "sub" : "subs"
      this._showToast(`Invites sent to ${data.invites.length} ${noun}`)
    } catch (e) {
      this._setError(this.inviteModalErrorTarget, e.message)
    } finally {
      this.inviteSendBtnTarget.disabled = false
      this.inviteSendBtnTarget.textContent = "Send Invites"
    }
  }

  async resendInvite(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.inviteId
    try {
      const res = await fetch(this._fromTemplate(this.inviteResendUrlTemplateValue, id), {
        method: "POST",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to resend invite")
      this._showToast("Invite resent")
    } catch (e) {
      this._showToast(e.message, "error")
    }
  }

  showInviteDeleteModal(event) {
    event.preventDefault()
    this._pendingInviteId = event.currentTarget.dataset.inviteId
    this.inviteDeleteNameTarget.textContent = event.currentTarget.dataset.inviteName || "This sub"
    this.inviteDeleteModalTarget.classList.remove("hidden")
  }

  hideInviteDeleteModal(event) {
    event?.preventDefault()
    this.inviteDeleteModalTarget.classList.add("hidden")
    this._pendingInviteId = null
  }

  async confirmInviteDelete(event) {
    event.preventDefault()
    const id = this._pendingInviteId
    if (!id) return

    try {
      const res = await fetch(this._fromTemplate(this.inviteDeleteUrlTemplateValue, id), {
        method: "DELETE",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to remove sub")

      document.getElementById(`sub-invite-${id}`)?.remove()
      if (!this.invitesTbodyTarget.querySelector("tr")) {
        this.invitesWrapTarget.classList.add("hidden")
        this.invitesEmptyTarget.classList.remove("hidden")
      }
      this._showToast("Sub removed")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.hideInviteDeleteModal()
    }
  }

  // ── Q&A ─────────────────────────────────────────────────────────────

  // Save (or edit) the draft answer on a sub's question — nothing is sent
  // to bidders until Publish.
  async saveAnswer(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.questionId
    const textarea = this.element.querySelector(`[data-question-answer-for="${id}"]`)
    if (!textarea) return

    try {
      const res = await fetch(this._fromTemplate(this.questionAnswerUrlTemplateValue, id), {
        method: "PATCH",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ answer_body: textarea.value })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to save the answer")

      this._swapQuestionRow(id, data.row)
      this._showToast("Answer saved as draft")
    } catch (e) {
      this._showToast(e.message, "error")
    }
  }

  showPublishModal(event) {
    event.preventDefault()
    this._pendingQuestionId = event.currentTarget.dataset.questionId
    this.publishModalTarget.classList.remove("hidden")
  }

  hidePublishModal(event) {
    event?.preventDefault()
    this.publishModalTarget.classList.add("hidden")
    this._pendingQuestionId = null
  }

  // Publish = broadcast: emails (and texts) the answer to every invite,
  // asker anonymous. Saves any unsaved textarea edits first.
  async confirmPublish(event) {
    event.preventDefault()
    const id = this._pendingQuestionId
    if (!id) return

    this.publishConfirmBtnTarget.disabled = true
    try {
      const textarea = this.element.querySelector(`[data-question-answer-for="${id}"]`)
      if (textarea) {
        const saveRes = await fetch(this._fromTemplate(this.questionAnswerUrlTemplateValue, id), {
          method: "PATCH",
          headers: this._jsonHeaders(),
          body: JSON.stringify({ answer_body: textarea.value })
        })
        const saveData = await saveRes.json().catch(() => ({}))
        if (!saveRes.ok || !saveData.ok) throw new Error(saveData.error || "Failed to save the answer")
      }

      const res = await fetch(this._fromTemplate(this.questionPublishUrlTemplateValue, id), {
        method: "POST",
        headers: this._jsonHeaders()
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to publish the answer")

      this._swapQuestionRow(id, data.row)
      this._showToast("Answer published — every bidder was notified")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.publishConfirmBtnTarget.disabled = false
      this.hidePublishModal()
    }
  }

  // GC-authored Q&A: question + answer together, publishes immediately.
  showQaModal(event) {
    event.preventDefault()
    this.qaModalQuestionTarget.value = ""
    this.qaModalAnswerTarget.value = ""
    this._clearError(this.qaModalErrorTarget)
    this.qaModalTarget.classList.remove("hidden")
    this.qaModalQuestionTarget.focus()
  }

  hideQaModal(event) {
    event?.preventDefault()
    this.qaModalTarget.classList.add("hidden")
  }

  async submitQaModal(event) {
    event.preventDefault()
    const body = this.qaModalQuestionTarget.value.trim()
    const answer = this.qaModalAnswerTarget.value.trim()
    if (!body || !answer) {
      this._setError(this.qaModalErrorTarget, "Add both the question and the answer.")
      return
    }

    this.qaModalSaveBtnTarget.disabled = true
    try {
      const res = await fetch(this.questionCreateUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ body, answer_body: answer })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to post the Q&A")

      this.questionsListTarget.insertAdjacentHTML("afterbegin", data.row)
      this.questionsListTarget.classList.remove("hidden")
      this.questionsEmptyTarget.classList.add("hidden")
      this.hideQaModal()
      this._showToast("Q&A published — every bidder was notified")
    } catch (e) {
      this._setError(this.qaModalErrorTarget, e.message)
    } finally {
      this.qaModalSaveBtnTarget.disabled = false
    }
  }

  _swapQuestionRow(id, rowHtml) {
    const existing = document.getElementById(`bid-question-${id}`)
    if (existing) {
      existing.outerHTML = rowHtml
    } else {
      this.questionsListTarget.insertAdjacentHTML("afterbegin", rowHtml)
    }
    this.questionsListTarget.classList.remove("hidden")
    this.questionsEmptyTarget.classList.add("hidden")
  }

  // ── Bids (leveling) tab — history, revision, award, notify ─────────

  // Feature 12/59 — AI bid check. Sync request with a spinner on the
  // button and a 90s abort so it can never hang; the flags panel swaps in
  // place above the grid.
  async runAiCheck(event) {
    event.preventDefault()
    const btn = this.aiCheckBtnTarget
    const originalHtml = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = '<svg class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg><span>Checking bids…</span>'

    const abort = new AbortController()
    const timer = setTimeout(() => abort.abort(), 90000)
    try {
      const res = await fetch(this.aiCheckUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        signal: abort.signal
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "The AI couldn't check these bids right now — please try again.")

      if (this.hasAiCheckPanelTarget) this.aiCheckPanelTarget.innerHTML = data.panel
      const n = data.flags_count || 0
      this._showToast(n > 0 ? `AI check complete — ${n} ${n === 1 ? "flag" : "flags"} to review` : "AI check complete — no flags")
    } catch (e) {
      const message = e.name === "AbortError"
        ? "The AI check timed out — please try again in a minute."
        : e.message
      this._showToast(message, "error")
    } finally {
      clearTimeout(timer)
      btn.disabled = false
      btn.innerHTML = originalHtml
    }
  }

  // Per-column version-history expander on the leveling grid.
  toggleHistory(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const block = this.element.querySelector(`[data-history-for="${btn.dataset.inviteId}"]`)
    if (!block) return
    const nowHidden = block.classList.toggle("hidden")
    btn.textContent = nowHidden ? btn.dataset.showLabel : "Hide history"
  }

  // Feature 65 — request a revised bid from one sub.
  showRevisionModal(event) {
    event.preventDefault()
    this._pendingRevisionInviteId = event.currentTarget.dataset.inviteId
    this.revisionNameTarget.textContent = event.currentTarget.dataset.inviteName || "This sub"
    this.revisionMessageTarget.value = ""
    this._clearError(this.revisionErrorTarget)
    this.revisionModalTarget.classList.remove("hidden")
    this.revisionMessageTarget.focus()
  }

  hideRevisionModal(event) {
    event?.preventDefault()
    this.revisionModalTarget.classList.add("hidden")
    this._pendingRevisionInviteId = null
  }

  async submitRevision(event) {
    event.preventDefault()
    const id = this._pendingRevisionInviteId
    if (!id) return

    this.revisionSendBtnTarget.disabled = true
    try {
      const res = await fetch(this._fromTemplate(this.revisionUrlTemplateValue, id), {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ message: this.revisionMessageTarget.value.trim() })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to request the revision")

      this._applyBidPanels(data)
      if (data.invite_row) {
        const existing = document.getElementById(`sub-invite-${id}`)
        if (existing) existing.outerHTML = data.invite_row
      }
      this.hideRevisionModal()
      this._showToast("Revised bid requested — the sub was notified")
    } catch (e) {
      this._setError(this.revisionErrorTarget, e.message)
    } finally {
      this.revisionSendBtnTarget.disabled = false
    }
  }

  // Feature 8 — award confirm modal. The per-bid body (sub, amount,
  // compliance warning) is server-rendered into a hidden block in the
  // leveling grid; we copy the matching one in.
  showAwardModal(event) {
    event.preventDefault()
    const bidId = event.currentTarget.dataset.bidId
    const source = this.element.querySelector(`[data-award-source="${bidId}"]`)
    if (!source) return

    this._pendingAwardBidId = bidId
    this.awardModalBodyTarget.innerHTML = source.innerHTML
    this._clearError(this.awardErrorTarget)
    this.awardModalTarget.classList.remove("hidden")
  }

  hideAwardModal(event) {
    event?.preventDefault()
    this.awardModalTarget.classList.add("hidden")
    this._pendingAwardBidId = null
  }

  async confirmAward(event) {
    event.preventDefault()
    const bidId = this._pendingAwardBidId
    if (!bidId) return

    this.awardConfirmBtnTarget.disabled = true
    this.awardConfirmBtnTarget.textContent = "Awarding..."
    try {
      const res = await fetch(this.awardUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ sub_bid_id: bidId })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to award the package")

      this._applyBidPanels(data)
      this.hideAwardModal()
      this._showToast("Package awarded — the winner was notified")
      // Offer the notify-losers step right away (they can pick "Not Now").
      if (data.losers_to_notify > 0) this._openNotifyModal()
    } catch (e) {
      this._setError(this.awardErrorTarget, e.message)
    } finally {
      this.awardConfirmBtnTarget.disabled = false
      this.awardConfirmBtnTarget.textContent = "Award Package"
    }
  }

  showUndoAwardModal(event) {
    event.preventDefault()
    this.undoAwardModalTarget.classList.remove("hidden")
  }

  hideUndoAwardModal(event) {
    event?.preventDefault()
    this.undoAwardModalTarget.classList.add("hidden")
  }

  async confirmUndoAward(event) {
    event.preventDefault()
    this.undoAwardConfirmBtnTarget.disabled = true
    try {
      const res = await fetch(this.undoAwardUrlValue, { method: "POST", headers: this._jsonHeaders() })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to undo the award")

      this._applyBidPanels(data)
      this._showToast("Award undone — the package is closed again")
    } catch (e) {
      this._showToast(e.message, "error")
    } finally {
      this.undoAwardConfirmBtnTarget.disabled = false
      this.hideUndoAwardModal()
    }
  }

  // Feature 68 — notify losing bidders. The recipient list is
  // server-rendered into the hidden notifyListSource block in the panel.
  showNotifyModal(event) {
    event.preventDefault()
    this._openNotifyModal()
  }

  _openNotifyModal() {
    if (this.hasNotifyListSourceTarget) {
      this.notifyModalBodyTarget.innerHTML = this.notifyListSourceTarget.innerHTML
      const count = parseInt(this.notifyListSourceTarget.dataset.notifyCount || "0", 10)
      this.notifyConfirmBtnTarget.classList.toggle("hidden", count === 0)
    }
    this._clearError(this.notifyErrorTarget)
    this.notifyModalTarget.classList.remove("hidden")
  }

  hideNotifyModal(event) {
    event?.preventDefault()
    this.notifyModalTarget.classList.add("hidden")
  }

  async confirmNotify(event) {
    event.preventDefault()
    this.notifyConfirmBtnTarget.disabled = true
    this.notifyConfirmBtnTarget.textContent = "Sending..."
    try {
      const res = await fetch(this.notifyLosersUrlValue, { method: "POST", headers: this._jsonHeaders() })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || "Failed to notify the other bidders")

      this._applyBidPanels(data)
      this.hideNotifyModal()
      const n = data.notified || 0
      this._showToast(n > 0 ? `${n} ${n === 1 ? "bidder" : "bidders"} notified` : "Everyone was already notified")
    } catch (e) {
      this._setError(this.notifyErrorTarget, e.message)
    } finally {
      this.notifyConfirmBtnTarget.disabled = false
      this.notifyConfirmBtnTarget.textContent = "Send Notices"
    }
  }

  // Swap whichever server-rendered fragments came back (leveling panel,
  // award banner, status pill) and sync the open/close buttons — DOM-in-
  // place, never a reload.
  _applyBidPanels(data) {
    if (data.panel !== undefined && this.hasBidsPanelTarget) this.bidsPanelTarget.innerHTML = data.panel
    if (data.banner !== undefined && this.hasAwardBannerTarget) this.awardBannerTarget.innerHTML = data.banner
    if (data.pill && this.hasStatusPillTarget) this.statusPillTarget.innerHTML = data.pill
    if (data.status) {
      const awarded = data.status === "awarded"
      const open = data.status === "open"
      this.openBtnTarget.classList.toggle("hidden", awarded || open)
      this.closeBtnTarget.classList.toggle("hidden", !open)
    }
  }

  // ── Shared helpers ──────────────────────────────────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  _fromTemplate(template, id) {
    // Templates carry a literal "0" placeholder segment, e.g. ".../documents/0"
    // or ".../invites/0/resend" — swap it for the real id.
    return template.replace(/\/0(\/|$)/, `/${id}$1`)
  }

  _setDate(value) {
    const input = this.pkgDueTarget
    input.value = value || ""
    if (input._flatpickr) input._flatpickr.setDate(value || null, false)
  }

  _setError(el, message) {
    el.textContent = message
    el.classList.remove("hidden")
  }

  _clearError(el) {
    el.textContent = ""
    el.classList.add("hidden")
  }

  _jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
